import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import { billingApi } from '../../services/billing';

export default function VendorBillingPayment() {
  const { data: paymentMethods, isLoading } = useQuery({
    queryKey: ['billing', 'payment-methods'],
    queryFn: () => billingApi.getPaymentMethods().then((r) => r.data),
  });

  const portalMutation = useMutation({
    mutationFn: () => billingApi.createPortalSession(window.location.href),
    onSuccess: (res) => {
      window.location.href = res.data.portal_url;
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const results = paymentMethods?.results ?? [];

  return (
    <>
      <PageHeader
        title="Payment Methods"
        actions={
          <Link
            to="/vendor/billing"
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:bg-slate-50"
          >
            Back to Billing
          </Link>
        }
      />

      {results.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <EmptyState
            title="No payment methods"
            description="Add a payment method to manage your subscription."
            icon={
              <svg className="h-6 w-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
              </svg>
            }
            action={
              <button
                onClick={() => portalMutation.mutate()}
                disabled={portalMutation.isPending}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl px-4 py-2 text-sm font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50"
              >
                {portalMutation.isPending ? 'Redirecting...' : 'Add Payment Method'}
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-4">
          {results.map((method) => (
            <div
              key={method.id}
              className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-slate-100">
                  <span className="text-sm font-bold text-slate-600 capitalize">
                    {method.card_brand}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    ****{method.card_last4}
                  </p>
                  <p className="text-xs text-slate-500">
                    Expires {String(method.exp_month).padStart(2, '0')}/{String(method.exp_year).slice(-2)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {method.is_default && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                    Default
                  </span>
                )}
              </div>
            </div>
          ))}

          <div className="pt-2">
            <button
              onClick={() => portalMutation.mutate()}
              disabled={portalMutation.isPending}
              className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl px-4 py-2 text-sm font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50"
            >
              {portalMutation.isPending ? 'Redirecting...' : 'Manage Payment Methods'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
